import { DataSourceRequestState, SortDescriptor, toDataSourceRequestString } from '@progress/kendo-data-query';
import { formatNumber } from '@progress/kendo-intl';
import { Button } from '@progress/kendo-react-buttons';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import {
  Grid,
  GridColumn as Column,
  GridCustomCellProps,
  GridEvent,
  GridHandle,
  GridSortChangeEvent,
  GridToolbar,
} from '@progress/kendo-react-grid';
import { Input, NumericTextBox } from '@progress/kendo-react-inputs';
import { arrowRotateCwIcon, filterClearIcon, pencilIcon, plusIcon } from '@progress/kendo-svg-icons';
import dayjs from 'dayjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import CustomerInput from 'components/CustomerInput';
import { GridLoadingPanel } from 'components/GridLoadingPanel';
import SimpleDatePicker from 'components/SimpleDatePicker';
import LocalDateCell from 'components/cells/LocalDateCell';
import { paths } from '../../../routes';
import { fetchApi } from 'services/api';
import { openWindowPerc } from 'services/openWindow';
import { Title } from 'utils/title';
import { CustomerCollectionAlertStatus } from 'TypeGen/AccountsReceivable/Collections/customer-collection-alert-status';
import { CustomerCollectionContactMethod } from 'TypeGen/AccountsReceivable/Collections/customer-collection-contact-method';
import { ViewModel as CustomerCollectionActivityViewModel } from 'TypeGen/AccountsReceivable/Collections/view-model';
import CollectionActivityDialog from './CollectionActivityDialog';
import { formatAlertStatus } from './alertStatuses';
import { contactMethodOptions, formatContactMethod } from './contactMethods';
import { CustomerNavBar } from '../../Customers/NavBar';

const PageSize = 50;
const SessionStorageName = 'CustomerCollectionActivitiesGridV2';
const defaultSort: SortDescriptor[] = [
  { field: 'ActivityDateTime', dir: 'desc' },
];

type CollectionResult = {
  Data?: CustomerCollectionActivityViewModel[];
  Total?: number;
};

type CollectionFilters = {
  ActivityFromDateTime: Date | null;
  ActivityToDateTime: Date | null;
  CreatedByName: string;
  CustomerID: number;
  CustomerNumber: string;
  CustomerName: string;
  ContactPerson: string;
  ContactMethod: CustomerCollectionContactMethod | null;
  ContactReached: boolean | null;
  AmountPastDue: number | null;
  PromiseFromDate: Date | null;
  PromiseToDate: Date | null;
  PromiseAmount: number | null;
};

type StoredGridSettings = {
  filters?: CollectionFilters;
  sort?: SortDescriptor[];
  mineOnly?: boolean;
};

const defaultFilters: CollectionFilters = {
  ActivityFromDateTime: null,
  ActivityToDateTime: null,
  CreatedByName: '',
  CustomerID: 0,
  CustomerNumber: '',
  CustomerName: '',
  ContactPerson: '',
  ContactMethod: null,
  ContactReached: null,
  AmountPastDue: null,
  PromiseFromDate: null,
  PromiseToDate: null,
  PromiseAmount: null,
};

const getInitialSettings = (sessionStorageName: string): StoredGridSettings => {
  try {
    const value = sessionStorage.getItem(sessionStorageName);
    if (!value) return {};
    const stored = JSON.parse(value) as StoredGridSettings;
    return {
      ...stored,
      // Older saved settings included this hidden server-side tie-breaker.
      sort: stored.sort?.filter(sort => sort.field !== 'CustomerCollectionActivityID'),
      filters: {
        ...defaultFilters,
        ...stored.filters,
        ActivityFromDateTime: stored.filters?.ActivityFromDateTime ? new Date(stored.filters.ActivityFromDateTime) : null,
        ActivityToDateTime: stored.filters?.ActivityToDateTime ? new Date(stored.filters.ActivityToDateTime) : null,
        PromiseFromDate: stored.filters?.PromiseFromDate ? new Date(stored.filters.PromiseFromDate) : null,
        PromiseToDate: stored.filters?.PromiseToDate ? new Date(stored.filters.PromiseToDate) : null,
      },
    };
  } catch {
    return {};
  }
};

const contactMethodFilterOptions = [
  { ID: -1, Name: 'Any Contact Method' },
  ...contactMethodOptions,
];

const contactReachedFilterOptions = [
  { ID: -1, Name: 'Any Response' },
  { ID: 1, Name: 'Reached / Responded' },
  { ID: 0, Name: 'Not Reached' },
];

const CustomerNumberCell = (props: GridCustomCellProps) => {
  const activity = props.dataItem as CustomerCollectionActivityViewModel;
  const path = paths.CustomerProfile.replace(':customerId', activity.CustomerID.toString());
  return (
    <td {...props.tdProps}>
      <a
        className="text-primary"
        href={path}
        onClick={event => {
          event.preventDefault();
          openWindowPerc(path);
        }}
      >
        {activity.CustomerNumber}
      </a>
    </td>
  );
};

const ContactMethodCell = (props: GridCustomCellProps) => (
  <td {...props.tdProps}>{formatContactMethod(props.dataItem.ContactMethod)}</td>
);

const CustomerNameCell = (props: GridCustomCellProps) => {
  const activity = props.dataItem as CustomerCollectionActivityViewModel;
  const path = paths.AccountsReceivableOutstandingInvoices.replace(':customerId', activity.CustomerID.toString());
  return (
    <td {...props.tdProps}>
      <a
        className="text-primary"
        href={path}
        onClick={event => {
          event.preventDefault();
          openWindowPerc(path, .9, .85, `CustomerOutstandingInvoices:${activity.CustomerID}`);
        }}
      >
        {activity.CustomerName}
      </a>
    </td>
  );
};

const AlertStatusCell = (props: GridCustomCellProps) => {
  const alertStatus = props.dataItem.AlertStatus as CustomerCollectionAlertStatus;
  const theme = alertStatus === CustomerCollectionAlertStatus.Urgent
    ? 'danger'
    : alertStatus === CustomerCollectionAlertStatus.Moderate
      ? 'warning'
      : alertStatus === CustomerCollectionAlertStatus.Low
        ? 'success'
        : 'secondary';

  return (
    <td {...props.tdProps}>
      <span className={`badge badge-${theme}`}>{formatAlertStatus(alertStatus)}</span>
    </td>
  );
};

const ContactReachedCell = (props: GridCustomCellProps) => (
  <td {...props.tdProps}>
    <span className={`badge badge-${props.dataItem.ContactReached ? 'success' : 'secondary'}`}>
      {props.dataItem.ContactReached ? 'Yes' : 'No'}
    </span>
  </td>
);

const PromiseDateCell = (props: GridCustomCellProps) => {
  const value = props.dataItem.PromiseToPayDate as string | null;
  return <td {...props.tdProps}>{value ? dayjs(value).format('MM/DD/YYYY') : ''}</td>;
};

const NoteCell = (props: GridCustomCellProps) => {
  const note = props.dataItem.Note as string | null;
  return (
    <td {...props.tdProps} title={note ?? undefined} style={{ whiteSpace: 'normal', lineHeight: 1.25 }}>
      <div style={{ maxHeight: '3.75em', overflow: 'hidden' }}>{note}</div>
    </td>
  );
};

type RouteComponentParams = {
  customerId?: string;
};

const CustomerCollectionActivities = () => {
  const { customerId: customerIdParam } = useParams<RouteComponentParams>();
  const parsedCustomerId = Number(customerIdParam);
  const customerId = Number.isInteger(parsedCustomerId) && parsedCustomerId > 0 ? parsedCustomerId : 0;
  const sessionStorageName = customerId > 0 ? `${SessionStorageName}:${customerId}` : SessionStorageName;
  const [initialSettings] = useState<StoredGridSettings>(() => getInitialSettings(sessionStorageName));
  const [dataState, setDataState] = useState<DataSourceRequestState>({
    skip: 0,
    take: PageSize,
    sort: initialSettings.sort?.length ? initialSettings.sort : defaultSort,
  });
  const [filters, setFilters] = useState<CollectionFilters>(initialSettings.filters ?? defaultFilters);
  const [mineOnly, setMineOnly] = useState(initialSettings.mineOnly ?? false);
  const [activities, setActivities] = useState<CustomerCollectionActivityViewModel[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [editingActivity, setEditingActivity] = useState<CustomerCollectionActivityViewModel | null | undefined>();
  const [gridStateVersion, setGridStateVersion] = useState(0);
  const gridRef = useRef<GridHandle>(null);
  const activityCountRef = useRef(0);
  const loadingRef = useRef(false);
  const requestSequence = useRef(0);

  useEffect(() => {
    activityCountRef.current = activities.length;
  }, [activities.length]);

  const fetchRecords = useCallback(async (append = false) => {
    if (append && loadingRef.current) return;

    const requestID = ++requestSequence.current;
    loadingRef.current = true;
    setLoading(true);
    if (!append) setLoadError(null);

    const requestState = structuredClone(dataState) as DataSourceRequestState;
    requestState.skip = append ? activityCountRef.current : 0;
    requestState.take = PageSize;

    try {
      sessionStorage.setItem(sessionStorageName, JSON.stringify({
        filters,
        sort: dataState.sort,
        mineOnly,
      } satisfies StoredGridSettings));
    } catch {
      // The grid still works when browser storage is unavailable.
    }

    if (!append) {
      gridRef.current?.element?.querySelector<HTMLElement>('.k-grid-content')?.scrollTo(0, 0);
    }

    try {
      const response: CollectionResult = await fetchApi(
        `/api/AccountsReceivable/Collections?${toDataSourceRequestString(requestState)}`,
        {
          CustomerID: customerId || null,
          Filters: {
            ...filters,
            ActivityFromDateTime: filters.ActivityFromDateTime
              ? dayjs(filters.ActivityFromDateTime).startOf('day').utc().toDate()
              : null,
            ActivityToDateTime: filters.ActivityToDateTime
              ? dayjs(filters.ActivityToDateTime).endOf('day').utc().toDate()
              : null,
            PromiseFromDate: filters.PromiseFromDate ? dayjs(filters.PromiseFromDate).format('YYYY-MM-DD') : null,
            PromiseToDate: filters.PromiseToDate ? dayjs(filters.PromiseToDate).format('YYYY-MM-DD') : null,
          },
          MineOnly: mineOnly,
        },
        'POST',
      );

      if (requestID !== requestSequence.current) return;

      const records = Array.isArray(response.Data) ? response.Data : [];
      setTotal(response.Total ?? 0);
      setActivities(current => append ? current.concat(records) : records);
    } catch {
      if (requestID === requestSequence.current) {
        setLoadError('Unable to load collection activities. Please refresh and try again.');
      }
    } finally {
      if (requestID === requestSequence.current) {
        loadingRef.current = false;
        setLoading(false);
      }
    }
  }, [customerId, dataState, filters, mineOnly, sessionStorageName]);

  useEffect(() => {
    requestSequence.current += 1;
    loadingRef.current = true;
    const timer = window.setTimeout(() => fetchRecords(false), 500);
    return () => window.clearTimeout(timer);
  }, [fetchRecords]);

  const onSortChange = (event: GridSortChangeEvent) => {
    setDataState(current => ({
      ...current,
      skip: 0,
      take: PageSize,
      sort: event.sort,
    }));
  };

  const onScroll = (event: GridEvent) => {
    const element = event.syntheticEvent.currentTarget as HTMLElement;
    const nearBottom = element.scrollHeight > element.clientHeight
      && element.scrollTop + element.clientHeight >= element.scrollHeight - 10;

    if (nearBottom && !loadingRef.current && activityCountRef.current < total) {
      fetchRecords(true);
    }
  };

  const resetGrid = () => {
    setDataState({ skip: 0, take: PageSize, sort: defaultSort });
    setGridStateVersion(current => current + 1);
    setFilters(defaultFilters);
    setMineOnly(false);
  };

  const EditCell = (props: GridCustomCellProps) => {
    const activity = props.dataItem as CustomerCollectionActivityViewModel;
    return (
      <td {...props.tdProps} className="text-center">
        {activity.CanModify && (
          <Button
            fillMode="flat"
            size="small"
            themeColor="primary"
            svgIcon={pencilIcon}
            title="Edit collection activity"
            aria-label="Edit collection activity"
            onClick={() => setEditingActivity(activity)}
          />
        )}
      </td>
    );
  };

  return (
    <>
      {customerId > 0 && <CustomerNavBar id={customerId} currentTab="Collections" />}
      <Title string={customerId > 0 ? "Customer Collection Management Log" : "Collection Management Log"} />
      {loadError && <div className="alert alert-danger mt-3 mb-0" role="alert">{loadError}</div>}
      <br />
      {loading && <GridLoadingPanel />}
      <Grid
        key={gridStateVersion}
        ref={gridRef}
        skip={dataState.skip}
        take={dataState.take}
        defaultSort={dataState.sort}
        data={activities}
        dataItemKey="CustomerCollectionActivityID"
        sortable={{ mode: 'multiple' }}
        resizable
        reorderable
        scrollable="scrollable"
        onSortChange={onSortChange}
        onScroll={onScroll}
        style={{ height: `calc(100vh - ${customerId > 0 ? 250 : 165}px)`, minHeight: 480 }}
      >
        <GridToolbar className="kendo-grid-toolbar-overflow-visible" overflow="none">
          {customerId === 0 && <div className="input-group" style={{ width: 220 }}>
            <CustomerInput
              placeholder="Customer ID"
              CustomerID={filters.CustomerID}
              CustomerNumber={filters.CustomerNumber}
              onChange={customer => setFilters(current => ({
                ...current,
                CustomerID: customer?.CustomerID ?? 0,
                CustomerNumber: customer?.CustomerNumber ?? '',
              }))}
            />
          </div>}
          {customerId === 0 && <Input
            style={{ width: 220 }}
            value={filters.CustomerName}
            placeholder="Customer Name"
            onChange={event => setFilters(current => ({ ...current, CustomerName: event.value?.toString() ?? '' }))}
          />}
          <Input
            style={{ width: 190 }}
            value={filters.CreatedByName}
            placeholder="Logged By"
            onChange={event => setFilters(current => ({ ...current, CreatedByName: event.value?.toString() ?? '' }))}
          />
          <Input
            style={{ width: 190 }}
            value={filters.ContactPerson}
            placeholder="Contact Person"
            onChange={event => setFilters(current => ({ ...current, ContactPerson: event.value?.toString() ?? '' }))}
          />
          <DropDownList
            style={{ width: 190 }}
            data={contactMethodFilterOptions}
            textField="Name"
            dataItemKey="ID"
            value={contactMethodFilterOptions.find(option => option.ID === (filters.ContactMethod ?? -1))}
            onChange={event => setFilters(current => ({
              ...current,
              ContactMethod: event.value.ID < 0 ? null : event.value.ID as CustomerCollectionContactMethod,
            }))}
          />
          <DropDownList
            style={{ width: 190 }}
            data={contactReachedFilterOptions}
            textField="Name"
            dataItemKey="ID"
            value={contactReachedFilterOptions.find(option => option.ID === (filters.ContactReached == null ? -1 : Number(filters.ContactReached)))}
            onChange={event => setFilters(current => ({
              ...current,
              ContactReached: event.value.ID < 0 ? null : event.value.ID === 1,
            }))}
          />
        </GridToolbar>
        <GridToolbar className="kendo-grid-toolbar-overflow-visible" overflow="none">
          <span>
            <label className="mb-1">Activity Date Range:</label>
            <span className="d-flex" style={{ gap: 6, alignItems: 'center' }}>
              <SimpleDatePicker
                value={filters.ActivityFromDateTime}
                inputProps={{ placeholder: 'From' }}
                onChange={value => setFilters(current => ({ ...current, ActivityFromDateTime: value }))}
              />
              -
              <SimpleDatePicker
                value={filters.ActivityToDateTime}
                inputProps={{ placeholder: 'To' }}
                onChange={value => setFilters(current => ({ ...current, ActivityToDateTime: value }))}
              />
            </span>
          </span>
          <span>
            <label className="mb-1">Promise to Pay Date Range:</label>
            <span className="d-flex" style={{ gap: 6, alignItems: 'center' }}>
              <SimpleDatePicker
                value={filters.PromiseFromDate}
                inputProps={{ placeholder: 'From' }}
                onChange={value => setFilters(current => ({ ...current, PromiseFromDate: value }))}
              />
              -
              <SimpleDatePicker
                value={filters.PromiseToDate}
                inputProps={{ placeholder: 'To' }}
                onChange={value => setFilters(current => ({ ...current, PromiseToDate: value }))}
              />
            </span>
          </span>
          <NumericTextBox
            width={170}
            value={filters.AmountPastDue}
            placeholder="Amount Past Due"
            format="c2"
            min={0}
            spinners={false}
            onChange={event => setFilters(current => ({ ...current, AmountPastDue: event.value ?? null }))}
          />
          <NumericTextBox
            width={170}
            value={filters.PromiseAmount}
            placeholder="Promise Amount"
            format="c2"
            min={0}
            spinners={false}
            onChange={event => setFilters(current => ({ ...current, PromiseAmount: event.value ?? null }))}
          />
        </GridToolbar>
        <GridToolbar className="kendo-grid-toolbar-overflow-visible" overflow="none">
          <Button
            svgIcon={filterClearIcon}
            title="Clear all filters and restore the default sort"
            onClick={resetGrid}
          />
          <Button
            svgIcon={arrowRotateCwIcon}
            title="Refresh"
            onClick={() => fetchRecords(false)}
          />
          <Button
            togglable
            selected={mineOnly}
            onClick={() => setMineOnly(value => !value)}
            title="Only show activities entered by me"
          >
            Mine
          </Button>
          <Button
            svgIcon={plusIcon}
            themeColor="primary"
            onClick={() => setEditingActivity(null)}
          >
            New Activity
          </Button>
        </GridToolbar>

        <Column field="ActivityDateTime" title="Activity Date" width={180} cells={{ data: LocalDateCell }} />
        <Column field="AlertStatus" title="Alert Status" width={125} cells={{ data: AlertStatusCell }} />
        <Column field="CreatedByName" title="Logged By" width={170} />
        <Column field="CustomerNumber" title="Customer ID" width={125} cells={{ data: CustomerNumberCell }} />
        <Column field="CustomerName" title="Customer" width={240} cells={{ data: CustomerNameCell }} />
        <Column field="ContactPerson" title="Contact Person" width={180} />
        <Column
          field="ContactMethod"
          title="Contact Method"
          width={170}
          sortable={false}
          cells={{ data: ContactMethodCell }}
        />
        <Column field="ContactReached" title="Reached / Responded" width={170} sortable={false} cells={{ data: ContactReachedCell }} />
        <Column field="AmountPastDue" title="Amount Past Due" format="{0:c2}" width={155} className="text-right" sortable={false} />
        <Column field="PromiseToPayDate" title="Promise to Pay Date" width={175} sortable={false} cells={{ data: PromiseDateCell }} />
        <Column field="PromiseAmount" title="Promise Amount" format="{0:c2}" width={155} className="text-right" sortable={false} />
        <Column field="Note" title="Notes" width={440} sortable={false} cells={{ data: NoteCell }} />
        <Column title="Edit" width={70} sortable={false} cells={{ data: EditCell }} />
      </Grid>

      <div className="k-pager k-pager-md k-grid-pager">
        <div style={{ marginLeft: 'auto' }}>
          {formatNumber(Math.min(activities.length, total), 'n0')} of {formatNumber(total, 'n0')} activities
        </div>
      </div>

      {editingActivity !== undefined && (
        <CollectionActivityDialog
          activity={editingActivity}
          customerId={customerId}
          onClose={() => setEditingActivity(undefined)}
          onSaved={() => {
            setEditingActivity(undefined);
            fetchRecords(false);
          }}
        />
      )}
    </>
  );
};

export default CustomerCollectionActivities;
